import { Component, computed, effect, input, signal } from '@angular/core';
import { Message, ThinkStep } from '../../../services/chat-service/types';
import { ReasoningLoader } from '../../../shared/reasoning-loader/reasoning-loader';
import { ReasoningTask } from '../../../shared/reasoning-loader/reasoning-task-list';

const STEPS_HOLD_OPEN_MS = 1000;

function hasDetail(step: ThinkStep): boolean {
  return !!step.detail?.trim();
}

function finishedLabel(label: string): string {
  return label.trim().replace(/(…|\.{3})$/, '');
}

function thinkStepTasks(events: ThinkStep[], thinking: boolean): ReasoningTask[] {
  const tasks: ReasoningTask[] = [];
  events.forEach((step, i) => {
    const description = step.detail?.trim() || null;
    if (step.status !== 'running' || i < events.length - 1) {
      tasks.push({ title: finishedLabel(step.label), description, status: 'completed' });
    } else if (thinking) {
      tasks.push({ title: step.label.trim(), description, status: 'in-progress' });
    }
  });
  return tasks;
}

@Component({
  selector: 'bot-reasoning',
  template: `
    @if (showSteps()) {
      <reasoning-loader
        [label]="label()"
        [active]="thinking()"
        [thoughts]="thoughts()"
        [tasks]="tasks()"
        [expanded]="expanded()"
        (expandedChange)="userExpanded.set($event)"
      />
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  imports: [ReasoningLoader],
})
export class BotReasoning {
  readonly message = input.required<Message>();

  // Steps come from the live stream or from saved history (null on older messages).
  readonly thinking = computed(() => !!this.message().steps && !this.message().content);
  private readonly thinkEvents = computed(
    () => this.message().thinkEvents ?? this.message().steps ?? [],
  );
  // A running step that never finished (e.g. a search that turned out to be small talk) is
  // dropped once the answer starts.
  private readonly finishedSteps = computed(() =>
    (this.message().steps ?? []).filter((s) => s.status !== 'running'),
  );

  private readonly usesTaskList = computed(() => this.thinkEvents().length > 1);
  readonly tasks = computed(() =>
    this.usesTaskList() ? thinkStepTasks(this.thinkEvents(), this.thinking()) : [],
  );
  readonly thoughts = computed(() => {
    if (this.usesTaskList()) {
      return [];
    }
    if (this.thinking()) {
      const latest = [...this.thinkEvents()].reverse().find(hasDetail);
      return latest ? [latest.detail!.trim()] : [];
    }
    const steps = this.finishedSteps();
    return steps.some(hasDetail)
      ? steps.filter(hasDetail).map((s) => s.detail!.trim())
      : steps.map((s) => finishedLabel(s.label));
  });
  readonly label = computed(() => {
    if (!this.thinking()) {
      return 'What I did';
    }
    return this.thinkEvents().at(-1)?.label.trim() || 'Thinking…';
  });
  readonly showSteps = computed(
    () => this.thinking() || this.tasks().length > 0 || this.thoughts().length > 0,
  );

  // Open while thinking, and for a moment after the answer starts so the last step (usually
  // the match result) can be read before it auto-collapses — unless the user toggled it.
  readonly userExpanded = signal<boolean | null>(null);
  private readonly holdOpen = signal(false);
  readonly expanded = computed(() => this.userExpanded() ?? (this.thinking() || this.holdOpen()));

  constructor() {
    // Only a reply seen thinking in this session holds open; history loads collapsed.
    let wasThinking = false;
    effect((onCleanup) => {
      if (this.thinking()) {
        wasThinking = true;
      } else if (wasThinking) {
        wasThinking = false;
        this.holdOpen.set(true);
        const timer = setTimeout(() => this.holdOpen.set(false), STEPS_HOLD_OPEN_MS);
        onCleanup(() => clearTimeout(timer));
      }
    });
  }
}
